/** Where and with which model an AI job runs: a machine (or let the hub pick) and a model, defaulting to the hub's AI settings. */
import { useEffect, useState } from 'react';
import { Select, formatMoney } from '@orgus/ui';
import { useMachines } from '../../lib/store.ts';
import { useAiJobs } from './data.ts';

export const AI_MODELS = [
  // '' runs the machine's own Claude Code default: always available, whatever the account or deployment.
  { value: '', label: 'Your default' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'opus', label: 'Opus' },
  { value: 'haiku', label: 'Haiku' },
];

export interface AiRun {
  machineId: string;
  model: string;
  setMachineId: (v: string) => void;
  setModel: (v: string) => void;
  /** What to send: only what differs from "let the hub pick". */
  value: { machineId?: string; model?: string };
}

export function useAiRunOptions(open: boolean): AiRun {
  const { state } = useAiJobs();
  const [machineId, setMachineId] = useState('');
  const [model, setModel] = useState('');
  useEffect(() => {
    if (!open) return;
    setMachineId('');
    setModel(state?.settings.model ?? '');
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return { machineId, model, setMachineId, setModel, value: { ...(machineId ? { machineId } : {}), ...(model ? { model } : {}) } };
}

export function AiRunOptions({ machineId, model, setMachineId, setModel }: AiRun) {
  const machines = useMachines();
  const { state } = useAiJobs();
  const online = machines.filter((m) => m.online);
  const local = online.find((m) => m.local);
  const budget = state?.settings.maxBudgetUsd ?? 2;
  const models = AI_MODELS.some((m) => m.value === model) || !model ? AI_MODELS : [...AI_MODELS, { value: model, label: model }];
  const where = machineId ? machines.find((m) => m.id === machineId)?.name : (local ?? online[0])?.name;
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label="Run on"
          value={machineId}
          onValueChange={setMachineId}
          options={[{ value: '', label: local ? `Pick for me (${local.name})` : 'Pick for me' }, ...machines.map((m) => ({ value: m.id, label: m.online ? m.name : `${m.name} (offline)`, disabled: !m.online }))]}
        />
        <Select label="Model" value={model} onValueChange={setModel} options={models} />
      </div>
      <p className="text-caption text-fg-subtle">
        {online.length
          ? `Runs Claude Code headless on ${where ?? 'a machine'} with that machine's own login, no API key. It stops at ${formatMoney(budget)} per job; the cost shows when it finishes.`
          : 'No machine is online. Start Orgus on a machine where Claude Code is installed and signed in.'}
      </p>
    </div>
  );
}
